<!-- Hero: panel left, image right -->
<section class="bg-forest">
  <div class="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2">
    <div class="flex items-center px-4 py-16 sm:px-6 sm:py-20 lg:px-12 lg:py-0 xl:px-16">
      <div class="max-w-md">
        <p class="text-sm font-medium uppercase tracking-wide text-rose">For sellers</p>
        <h1 class="mt-4 font-display text-4xl font-semibold text-cream sm:text-5xl">
          Give your saddle a second rider.
        </h1>
        <p class="mt-5 text-lg text-cream/75">
          List in minutes, free of charge. Reach buyers backed by protected payments — you only
          pay a commission once it actually sells.
        </p>

        <div class="mt-9 flex flex-wrap items-center gap-4">
          <button
            type="button"
            (click)="startListing()"
            class="cursor-pointer rounded-full bg-rose px-6 py-3 text-sm font-medium text-cream transition hover:opacity-90"
          >
            List your saddle free
          </button>
          <a
            routerLink="/shop"
            class="rounded-full border border-cream/40 px-6 py-3 text-sm font-medium text-cream transition hover:border-cream"
          >
            See what's selling
          </a>
        </div>

        <p class="mt-6 text-sm text-cream/55">0% to list. 8% only when it sells.</p>
      </div>
    </div>

    <div class="order-first aspect-[4/5] overflow-hidden lg:order-last lg:aspect-auto">
      <img
        src="assets/marketing/sell-hero.webp"
        alt="A western saddle resting on a fence rail at golden hour"
        fetchpriority="high"
        class="h-full w-full object-cover"
      />
    </div>
  </div>
</section>

<!-- Trust strip -->
<section class="border-t border-cream/10 bg-forestdark text-cream">
  <div
    class="mx-auto grid max-w-6xl grid-cols-2 gap-x-4 gap-y-8 px-4 py-10 sm:gap-6 sm:px-6 sm:py-8 lg:grid-cols-4 lg:gap-8 lg:px-8"
  >
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="dollar" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">Free to list</p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">No upfront cost, ever</p>
      </div>
    </div>
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="shield" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">Verified buyers</p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">Real accounts, secure payments</p>
      </div>
    </div>
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="clock" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">48-hour response</p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">You accept or decline every offer</p>
      </div>
    </div>
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="check" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">Protected payments</p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">Buyers commit with confidence</p>
      </div>
    </div>
  </div>
</section>

<!-- How it works -->
<section class="mx-auto max-w-5xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8" appReveal>
  <p class="mb-3 text-center text-sm font-medium uppercase tracking-wide text-tandark">
    How it works
  </p>
  <h2 class="text-center font-display text-3xl font-semibold text-ink sm:text-4xl">
    From listing to sold, in three steps
  </h2>

  <div class="mt-14 grid grid-cols-1 gap-10 sm:grid-cols-3 sm:gap-8">
    <div class="flex flex-col items-center text-center sm:items-start sm:text-left">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose font-display font-semibold text-cream"
        >1</span
      >
      <p class="mb-1 mt-4 font-semibold text-ink">List your saddle</p>
      <p class="text-sm text-ink/70">
        Add photos, a description, and your price — free, and live once we review it.
      </p>
    </div>
    <div class="flex flex-col items-center text-center sm:items-start sm:text-left">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose font-display font-semibold text-cream"
        >2</span
      >
      <p class="mb-1 mt-4 font-semibold text-ink">Buyers offer or buy now</p>
      <p class="text-sm text-ink/70">
        You choose — accept, decline, or let it expire. No back-and-forth over email or DMs.
      </p>
    </div>
    <div class="flex flex-col items-center text-center sm:items-start sm:text-left">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose font-display font-semibold text-cream"
        >3</span
      >
      <p class="mb-1 mt-4 font-semibold text-ink">Sale completes after inspection</p>
      <p class="text-sm text-ink/70">
        Funds are held securely through the buyer's 48-hour inspection window. Once it completes,
        we deduct our commission (8%, less for early sellers) and the sale is final.
      </p>
    </div>
  </div>
</section>

<!-- Cross-sell: Wanted Saddle -->
<section class="px-4 py-20 sm:px-6 sm:py-24 lg:px-8" appReveal>
  <div
    class="mx-auto grid max-w-6xl grid-cols-1 items-center overflow-hidden rounded-3xl border border-line bg-tanlight/30 lg:grid-cols-2"
  >
    <div class="p-8 sm:p-12">
      <p class="text-sm font-medium uppercase tracking-wide text-tandark">Not ready to sell?</p>
      <h2 class="mt-3 font-display text-2xl font-semibold text-ink sm:text-3xl">
        Tell buyers what you're after instead.
      </h2>
      <p class="mt-4 text-ink/70">
        Trading up, or shopping for your next saddle too? Post a Wanted Saddle and let sellers come
        to you — no browsing required.
      </p>
      <a
        routerLink="/want-a-saddle"
        class="mt-7 inline-flex items-center gap-1.5 rounded-full bg-ink px-6 py-3 text-sm font-medium text-cream transition hover:opacity-90"
      >
        Explore Wanted Saddle
        <app-icon name="arrowRight" svgClass="h-4 w-4" [strokeWidth]="2" />
      </a>
    </div>
    <div class="order-first aspect-[4/3] overflow-hidden lg:order-last lg:aspect-auto lg:h-full">
      <img
        src="assets/marketing/wanted-hero.webp"
        alt="A western saddle and coiled rope, waiting"
        loading="lazy"
        class="h-full w-full object-cover"
      />
    </div>
  </div>
</section>

<!-- Final CTA -->
<section class="bg-creamdark/60 py-20 sm:py-24">
  <div class="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
    <h2 class="font-display text-3xl font-semibold text-ink sm:text-4xl">
      Ready to clear out the tack room?
    </h2>
    <p class="mt-4 text-ink/70">
      Free to list. 8% commission only if it sells. Takes about five minutes.
    </p>
    <button
      type="button"
      (click)="startListing()"
      class="mt-8 inline-block cursor-pointer rounded-full bg-forest px-8 py-3.5 text-sm font-medium text-cream transition hover:bg-forestdark"
    >
      List your saddle free
    </button>
  </div>
</section>
